import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'

import { createAiTest, createAiTestVariables } from '__generatedTypes__/createAiTest'
import { toast } from 'utils/toast'
import { CREATE_AI_TEST } from 'queries/responseTests'
import {
  AITestResponseTypeEnum,
  AITestTypeEnum,
  CreateAiTestInput,
  MembershipType,
} from '__generatedTypes__/globalTypes'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { QUERY_PARAM } from 'pagesNew/Users'
import { Form, FormValues } from './Form'

export const CreateResponseTest = () => {
  const [searchParams] = useSearchParams()
  const defaultOrder = Number(searchParams.get('defaultOrder')) ?? 0
  const fltId = searchParams.get('fltId') as string | null
  const type = searchParams.get('type') as AITestTypeEnum | null
  const responseType = searchParams.get('responseType') as AITestResponseTypeEnum | null
  const isFltTest = !!fltId

  const [createTest] = useMutation<createAiTest, createAiTestVariables>(CREATE_AI_TEST)
  const { navigate } = useLink()

  const goToTestsList = (type?: AITestTypeEnum | null) => {
    if (isFltTest) {
      navigate(cmsRoutes.RESPONSE_FULL_LENGTH_TEST, undefined, { replace: true })
      return
    }

    const searchParams = new URLSearchParams(type ? { [QUERY_PARAM]: type } : undefined)
    navigate(cmsRoutes.RESPONSE_TESTS, undefined, { searchParams: searchParams, replace: true })
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const question1 = {
        title: 'Question #1',
        questionText: values.question1,
        bestResponseText: values.textBestResponse1 || undefined,
        bestResponseVideoUrl: values.videoBestResponse1 || undefined,
        isLive: values.isLive1 ?? isFltTest,
        order: 0,
      }
      const question2 = {
        title: 'Question #2',
        questionText: values.question2,
        bestResponseText: values.textBestResponse2 || undefined,
        bestResponseVideoUrl: values.videoBestResponse2 || undefined,
        isLive: values.isLive2 ?? isFltTest,
        order: 1,
      }
      const question3 = {
        title: 'Question #3',
        questionText: values.question3,
        bestResponseText: values.textBestResponse3 || undefined,
        bestResponseVideoUrl: values.videoBestResponse3 || undefined,
        isLive: values.isLive3 ?? isFltTest,
        order: 2,
      }

      const data: CreateAiTestInput = {
        title: values.title,
        description: values.description,
        membershipLevel: values.membershipLevel ?? MembershipType.Free,
        contextText: values.contextText,
        contextVideoUrl: values.contextVideoUrl,
        videoText: values.videoText,
        type: type ?? values.type?.id,
        responseType: responseType ?? null,
        casperFullLengthTest: isFltTest ? { id: fltId } : null,
        order: defaultOrder,
        questions: isFltTest ? [question1, question2] : [question1, question2, question3],
      }

      await createTest({ variables: { input: data } })
      toast.success('Response Test created successfully')
      goToTestsList(type)
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        toast.error(String(error.message))
      } else {
        toast.error('Error while saving test')
      }
    }
  }

  useEffect(() => {
    if (isFltTest ? !responseType : !type) {
      goToTestsList()
    }
  }, [defaultOrder, isFltTest, type, responseType])

  return <Form onSubmit={handleSubmit} type={type} responseType={responseType} isFltTest={isFltTest} />
}
